/**
 * Appels des notifications (specs §6.7 et §6.8, archi §4.7 et §6.4).
 *
 * ⚠️ **Les heures voyagent telles quelles, dans le fuseau des notifications**,
 * contrairement à tout ce que fait le reste du front depuis les retours de
 * l'étape 8 (`browserToday()`). Ce n'est pas une incohérence :
 * `jobs/scheduler.ts` évalue ses conditions sans navigateur, et une heure
 * convertie à l'aller puis comparée à un autre fuseau au retour ne voudrait
 * plus rien dire. La réponse porte `serverTime` et `timeZone` précisément pour
 * que l'écran puisse le dire plutôt que de le cacher.
 *
 * 🔴 Depuis l'étape 11, ce fuseau **se règle** (`saveTimeZone`). Il ne se
 * devine pas : la recette finale a montré un bilan de 21 h 30 arrivant à
 * 6 h 30 du matin, faute d'avoir jamais dit au serveur où vit son utilisateur.
 */

import type {
  NotificationSettingInput,
  NotificationType,
  NotificationsResponse,
  PushSendResult,
  PushSubscriptionInput,
} from '../../../shared/types';
import { apiFetch } from './client';

/** Clé de cache du volet des Réglages. */
export const NOTIFICATIONS_KEY = ['notifications'] as const;

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** État complet : clé publique VAPID, les trois réglages, appareils abonnés. */
export function fetchNotifications(): Promise<NotificationsResponse> {
  return apiFetch<NotificationsResponse>('/api/notifications');
}

/** Activation et heure d'une notification — vaut pour tous les appareils. */
export function saveNotificationSetting(
  type: NotificationType,
  body: NotificationSettingInput,
): Promise<NotificationsResponse> {
  return send<NotificationsResponse>('PUT', `/api/notifications/${type}`, body);
}

/**
 * Pose le fuseau dans lequel les trois heures sont interprétées.
 *
 * `null` revient au fuseau de la machine (`TZ`).
 *
 * ⚠️ Ce réglage vaut pour le **compte**, pas pour l'appareil : il décrit où vit
 * l'utilisateur, pas quel téléphone il tient. C'est l'inverse du thème, que
 * l'étape 10 a délibérément laissé dans le stockage local du navigateur.
 */
export function saveTimeZone(timeZone: string | null): Promise<NotificationsResponse> {
  return send<NotificationsResponse>('PUT', '/api/notifications/timezone', { timeZone });
}

/**
 * Déclare l'abonnement de **cet** appareil.
 *
 * Rejoué à chaque ouverture de l'application quand la permission est accordée :
 * c'est la seule façon de rattraper un abonnement que le navigateur a renouvelé
 * de son côté, ou que le serveur a supprimé après un 404/410 (§6.4) alors que le
 * navigateur le croit toujours valide. Idempotent par `endpoint`.
 */
export function registerSubscription(
  body: PushSubscriptionInput,
): Promise<NotificationsResponse> {
  return send<NotificationsResponse>('POST', '/api/notifications/subscription', body);
}

/** Retire l'abonnement de cet appareil — les autres continuent de recevoir. */
export function unregisterSubscription(endpoint: string): Promise<NotificationsResponse> {
  return send<NotificationsResponse>('DELETE', '/api/notifications/subscription', {
    endpoint,
  });
}

/**
 * Envoi d'essai immédiat.
 *
 * Le seul moyen de vérifier qu'une notification arrive vraiment sur cet
 * appareil-ci sans régler une heure et attendre — et le seul moyen de
 * distinguer « le push ne marche pas » de « la condition n'était pas remplie ».
 */
export function sendTestNotification(): Promise<PushSendResult> {
  return send<PushSendResult>('POST', '/api/notifications/test');
}
